<script lang="ts">
	import { get } from 'svelte/store';
	import { format } from 'svelte-i18n';
	//Design system components
	import { Alert } from '@Shelly/shelly-design-system';
	//Components
	import VCModalSettings from '../../Settings/VCModalSettings.svelte';
	//Helpers
	import {
		validateVirtualComponents,
		validateVirtualTemplateComponents,
	} from 'src/helpers/virtual_helpers/template_helpers/virtual_template_validation';
	import { saveVCSettings } from 'src/helpers/virtual_helpers/virtual_components_settings_helpers';
	import { getComponentByDevice } from 'src/helpers/virtual_helpers/virtual_components_helpers';
	import { real_id } from 'src/helpers/interface_helpers';
	import { devices } from 'src/stores';
	//Types
	import type { Device } from 'src/types/Device';
	import type { ComponentType } from 'svelte';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { VCInterfaceStatus, VirtualComponentInterface } from 'src/types/VirtualComponents';
	import type { VirtualDeviceTemplate } from 'src/helpers/virtual_helpers/template_helpers/virtual_template_types';

	export let device: Device;
	export let statuses: VCInterfaceStatus[];
	export let template: VirtualDeviceTemplate | null = null;
	export let enableSettingsLinks = true;

	let selectedComponent: VirtualComponentInterface | null = null;
	let showVCModalSettings = false;
	let waitForSaveResponse = false;
	let templateParentDevice: Device | null = null;
	let templateGroupComponent: string | null = null;
	let showTemplateSettings = false;
	let showTemplateMainModal = false;
	let TemplateSettingsModal: ComponentType | null = null;

	const openComponentSettings = (component?: string) => {
		if (!component) return;
		const status = statuses.find((item) => item.component === component);
		if (!status?.config) return;

		selectedComponent = {
			key: component,
			config: status.config,
			status: { value: status.rawValue ?? status.event },
		};
		showVCModalSettings = true;
	};

	const openTemplateSettings = async () => {
		templateParentDevice = get(devices)[real_id(device.id)] || device;
		templateGroupComponent = getComponentByDevice(device);
		if (!templateGroupComponent) return;
		TemplateSettingsModal = (await import('./CreateTemplateModal.svelte')).default;
		showTemplateSettings = true;
	};

	const getAlertCTA = (settingsTarget?: 'component' | 'template', component?: string) => {
		if (!enableSettingsLinks) return undefined;
		if (settingsTarget === 'template') {
			return {
				title: $format('button_edit_template', {
					default: 'Edit template',
				}),
			};
		}
		if (!component || !statuses.find((status) => status.component === component)?.config) {
			return undefined;
		}
		return {
			title: $format('button_show_modal_component_settings', {
				default: 'Show component settings',
			}),
		};
	};

	const openAlertSettings = (settingsTarget?: 'component' | 'template', component?: string) => {
		if (settingsTarget === 'template') {
			void openTemplateSettings();
			return;
		}
		openComponentSettings(component);
	};

	const saveSettings = async (event: CustomEvent) => {
		waitForSaveResponse = true;
		const { settings, type } = event.detail;
		const parentDevice = get(devices)[real_id(device.id)] || device;
		const response: SettingRequest = await saveVCSettings(parentDevice, type, settings);
		if (response?.isok) showVCModalSettings = false;
		waitForSaveResponse = false;
	};

	$: validationErrors = template?.type
		? validateVirtualTemplateComponents(template, statuses)
		: validateVirtualComponents(statuses);
</script>

{#if validationErrors}
	{#each validationErrors as error}
		<Alert
			class="mb-s"
			isHeadingBold
			type={error.type}
			heading={error.heading}
			message={error.message}
			cta={getAlertCTA(error.settingsTarget, error.component)}
			on:cta_click={() => openAlertSettings(error.settingsTarget, error.component)}
		/>
	{/each}
{/if}

{#if selectedComponent && showVCModalSettings}
	<VCModalSettings
		component={selectedComponent}
		waitForResponse={waitForSaveResponse}
		bind:showModal={showVCModalSettings}
		on:edit={saveSettings}
	/>
{:else if TemplateSettingsModal && templateParentDevice && templateGroupComponent && showTemplateSettings}
	<svelte:component
		this={TemplateSettingsModal}
		device={templateParentDevice}
		groupComponent={templateGroupComponent}
		bind:showMainModal={showTemplateMainModal}
		bind:showCreateTemplateModal={showTemplateSettings}
	/>
{/if}
